import React, { useMemo } from 'react';
import { useTranslation } from 'localization';
import HistoryCardMolecule, {
  HistoryCardEntry,
  HistoryCardStatusDescriptor,
} from '../HistoryCard/HistoryCardMolecule';
import './HistoryStripMolecule.scss';

export type HistoryStripEntry = HistoryCardEntry;

export interface HistoryStripMoleculeProps {
  /**
   * History entries in the order returned by the history hook/endpoint
   * (oldest → newest). The molecule renders them newest → oldest.
   */
  entries: HistoryStripEntry[];
  /**
   * Status key → icon / color / localized-label descriptor. Passed through
   * to every `HistoryCardMolecule`.
   */
  statusColorMap: Record<string, HistoryCardStatusDescriptor>;
  /** Override the empty-state caption (defaults to `taskList.historyStrip.empty`). */
  emptyLabel?: string;
  /** Override the header title (defaults to `taskList.historyStrip.title`). */
  title?: string;
  /** When supplied, renders a close button in the header. */
  onClose?: () => void;
  /** Locale override for date formatting inside each card. */
  locale?: string;
  className?: string;
}

const HistoryStripMolecule: React.FC<HistoryStripMoleculeProps> = ({
  entries,
  statusColorMap,
  emptyLabel,
  title,
  onClose,
  locale,
  className = '',
}) => {
  const { t } = useTranslation();
  const resolvedTitle = title ?? t('taskList.historyStrip.title');
  const resolvedEmpty = emptyLabel ?? t('taskList.historyStrip.empty');
  const closeLabel = t('taskList.historyStrip.close');

  const orderedEntries = useMemo(
    () => (entries ? entries.slice().reverse() : []),
    [entries]
  );

  const isEmpty = orderedEntries.length === 0;

  const classes = ['history-strip-molecule', className]
    .filter(Boolean)
    .join(' ');

  return (
    <section className={classes} aria-label={resolvedTitle}>
      <header className="history-strip-molecule__header">
        <h4 className="history-strip-molecule__title">{resolvedTitle}</h4>
        {onClose && (
          <button
            type="button"
            className="history-strip-molecule__close"
            onClick={onClose}
            aria-label={closeLabel}
            title={closeLabel}
          >
            <i className="pi pi-times" aria-hidden="true" />
          </button>
        )}
      </header>
      {isEmpty ? (
        <p className="history-strip-molecule__empty">{resolvedEmpty}</p>
      ) : (
        <ul className="history-strip-molecule__list">
          {orderedEntries.map((entry, index) => (
            <li key={entry.periodId} className="history-strip-molecule__item">
              <HistoryCardMolecule
                entry={entry}
                statusColorMap={statusColorMap}
                isLatest={index === 0}
                locale={locale}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

HistoryStripMolecule.displayName = 'HistoryStripMolecule';

export default HistoryStripMolecule;
